import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Fingerprint, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loginPasskey, passkeySupported, inIframe } from "@/lib/passkey";

const BG = "https://images.pexels.com/photos/36162318/pexels-photo-36162318.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const u = await login(data.token);
      if (u?.must_change_password) {
        toast.info("Please set a new password to continue");
        navigate("/change-password");
      } else {
        toast.success("Welcome back");
        navigate("/");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && typeof detail === "string" && detail.toLowerCase().includes("passkey")) {
        toast.error("This account requires a passkey. Use 'Sign in with a passkey' below.");
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const passkeyLogin = async () => {
    if (inIframe()) {
      toast.error("Passkeys don't work inside the embedded preview. Open the app in its own browser tab to use a passkey.");
      return;
    }
    if (!passkeySupported()) return toast.error("This device/browser does not support passkeys.");
    try {
      const { token } = await loginPasskey(email || undefined);
      await login(token);
      toast.success("Signed in with passkey");
      navigate("/");
    } catch (err) {
      if (err.name === "NotAllowedError") return toast.error("Passkey sign-in was cancelled or timed out.");
      if (err.name === "SecurityError") return toast.error("Passkeys require the app on its own secure (HTTPS) domain.");
      const msg = err.response?.data?.detail || err.message || "Passkey sign-in failed";
      toast.error(formatApiErrorDetail(msg));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 text-white overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/75" />
        <div className="relative flex items-center gap-2.5">
          <img src="/renova-icon.png" alt="" className="w-11 h-11 object-contain bg-white/95 rounded-md p-1" />
          <span className="font-heading font-extrabold text-lg">Renova Eco Systems</span>
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
            One secure home for everything your company knows.
          </h1>
          <p className="text-white/70 leading-relaxed">
            Credentials, documents, media and your entire digital asset inventory — categorized,
            related and searchable in a single living model of the company.
          </p>
          <div className="space-y-3 pt-2">
            {[[KeyRound, "Encrypted secrets vault"], [ShieldCheck, "Expiration & security tracking"], [Fingerprint, "Related-asset company map"]].map(([I, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-white/85">
                <I className="w-4 h-4 text-[#7ac29a]" /> {t}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <img src="/renova-wordmark.png" alt="Renova Eco Systems" className="h-10 object-contain bg-white/95 rounded px-3 py-1.5" />
          </div>
        </div>
        <div className="relative text-xs text-white/40">© 2026 Renova Eco Systems</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/renova-icon.png" alt="" className="w-10 h-10 object-contain" />
            <span className="font-heading font-extrabold text-lg">Renova Eco Systems</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">Access your company vault</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-[#0A0A0A] hover:bg-gray-800 h-11">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <button
            type="button"
            data-testid="passkey-btn"
            onClick={passkeyLogin}
            className="w-full mt-3 h-11 flex items-center justify-center gap-2 rounded-md border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Fingerprint className="w-4 h-4" /> Sign in with a passkey
          </button>

          <p className="text-xs text-gray-400 mt-6 text-center">
            Accounts are provisioned by your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
